import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Smartphone, Youtube, HeartHandshake, Gift, ChevronRight, ArrowLeft } from "lucide-react";
import { api, imgSrc } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { LINKS } from "@/components/profile/FbProfile";
import Support from "@/pages/Support";
import { usePageMeta } from "@/lib/seo";

const YT_FALLBACK = "https://www.youtube.com/results?search_query=itzcouple";

const LinkBtn = ({ to, href, icon: Icon, title, sub, primary, testId, delay }) => {
  const cls = `group flex items-center gap-4 w-full p-4 rounded-2xl font-bold transition-transform duration-200 active:scale-[0.98] hover:-translate-y-0.5 fade-up ${primary ? "pink-gradient text-white shadow-xl shadow-rose-300/60" : "bg-white text-slate-800 border border-pink-100 shadow-md shadow-rose-100"}`;
  const inner = (<>
    <span className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${primary ? "bg-white/20" : "bg-rose-50 text-rose-500"}`}><Icon className="w-6 h-6" /></span>
    <span className="flex-1 min-w-0 text-left"><span className="block">{title}</span><span className={`block text-xs font-medium ${primary ? "text-white/85" : "text-slate-500"}`}>{sub}</span></span>
    <ChevronRight className={`w-5 h-5 transition-transform group-hover:translate-x-1 ${primary ? "" : "text-slate-300"}`} />
  </>);
  const style = { animationDelay: `${delay}ms` };
  return to ? <Link to={to} className={cls} style={style} data-testid={testId}>{inner}</Link>
    : <a href={href} target="_blank" rel="noopener noreferrer" className={cls} style={style} data-testid={testId}>{inner}</a>;
};

export default function BioLinks() {
  usePageMeta({ title: "Links von itzcouple", description: "Alle Links von itzcouple: Community-App, YouTube-Kanal und Projekt unterstützen – an einem Ort." });
  const [c, setC] = useState(null);
  const { user } = useAuth();
  useEffect(() => { api.get("/public/creator").then(({ data }) => setC(data)).catch(() => setC({ links: {} })); }, []);
  const links = c?.links || {};
  const yt = links.youtube ? LINKS.find(([k]) => k === "youtube")[2](links.youtube) : YT_FALLBACK;
  const socials = LINKS.filter(([k]) => k !== "youtube" && links[k]);
  return (
    <div className="min-h-screen noise-bg relative overflow-hidden" data-testid="bio-links-page">
      <div className="absolute -top-40 -right-40 w-[32rem] h-[32rem] rounded-full bg-fuchsia-300/40 blur-3xl pointer-events-none" />
      <div className="absolute top-1/2 -left-40 w-[28rem] h-[28rem] rounded-full bg-rose-300/40 blur-3xl pointer-events-none" />
      <main className="relative max-w-md mx-auto px-5 py-10 flex flex-col items-center text-center gap-6">
        <div className="relative fade-up">
          <span className="absolute -inset-3 rounded-full bg-gradient-to-br from-rose-400 via-pink-500 to-fuchsia-500 blur-xl opacity-60" />
          <img src={c?.picture ? imgSrc(c.picture) : "/logo-mark.png"} alt="itzcouple" className="relative w-28 h-28 rounded-full object-cover border-4 border-white shadow-xl bg-white" data-testid="bio-avatar" />
        </div>
        <div className="fade-up" style={{ animationDelay: "60ms" }}>
          <h1 className="text-3xl font-extrabold tracking-tight">itz<span className="text-rose-500">couple</span></h1>
          <p className="text-slate-600 mt-1">YouTube-Paar · Community · Geschenke 💖</p>
        </div>
        <span className="inline-flex items-center gap-2 px-4 h-9 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-xs font-bold fade-up" style={{ animationDelay: "100ms" }} data-testid="bio-perk"><Gift className="w-4 h-4" /> Videos schauen, Punkte sammeln, Geschenke holen</span>
        <div className="w-full space-y-3">
          <LinkBtn to={user ? "/feed" : "/"} icon={Smartphone} title="Zur Community-App" sub="Kostenlos mitmachen & Punkte sammeln" primary testId="bio-link-app" delay={140} />
          <LinkBtn href={yt} icon={Youtube} title="Unser YouTube-Kanal" sub="Neue Videos von uns" testId="bio-link-youtube" delay={200} />
          <LinkBtn to="/projekt-unterstuetzen" icon={HeartHandshake} title="Projekt Unterstützer werden" sub="Unterstütze die Community &amp; werde Premium-Mitglied" testId="bio-link-support" delay={260} />
        </div>
        {socials.length > 0 && (
          <div className="flex gap-3 fade-up" style={{ animationDelay: "320ms" }}>
            {socials.map(([k, Icon, url]) => <a key={k} href={url(links[k])} target="_blank" rel="noopener noreferrer" aria-label={k} data-testid={`bio-social-${k}`} className="w-12 h-12 rounded-full bg-white border border-pink-100 text-rose-500 shadow-sm flex items-center justify-center hover:scale-110 transition-transform"><Icon className="w-5 h-5" /></a>)}
          </div>
        )}
        <Logo to="/" />
      </main>
    </div>
  );
}

export function PublicSupport() {
  usePageMeta({ title: "Projekt Unterstützer werden", description: "Unterstütze die itzPlay Community: Erfahre, was hinter der App steckt, und hilf dem YouTube-Paar itzcouple mit einer Überweisung." });
  const { user } = useAuth();
  return (
    <div className="min-h-screen noise-bg" data-testid="public-support-page">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-pink-100">
        <div className="max-w-2xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/links" data-testid="public-support-back" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-rose-600"><ArrowLeft className="w-4 h-4" /> Links</Link>
          <Logo to="/" />
          <Link to={user ? "/feed" : "/registrieren"} data-testid="public-support-app" className="px-4 h-9 inline-flex items-center rounded-full pink-gradient text-white text-sm font-bold">{user ? "Zur App" : "Mitmachen"}</Link>
        </div>
      </header>
      <main className="px-4 py-8"><Support publicView /></main>
    </div>
  );
}
